import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useCondominio } from '../contexts/CondominioContext';
import logo from '../images/logo.png';
import {
  Home,
  Building2,
  Users,
  UserCheck,
  Package,
  AlertTriangle,
  BarChart3,
  Settings,
  Menu,
  X,
  LogOut,
  Bell,
  Calendar,
  UserCog,
  MapPin,
  Car,
  MessageSquare,
  UserPlus,
  Shield,
  UserX,
  FileText,
  Search,
  Heart,
  Clock,
  ChevronDown,
  ChevronRight,
  Briefcase,
  DollarSign,
  CreditCard,
  SlidersHorizontal,
  Receipt,
  Vote
} from 'lucide-react';

const Layout = ({ children }) => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [showCondominioSelector, setShowCondominioSelector] = useState(false);
  const [showProfileDropdown, setShowProfileDropdown] = useState(false);
  const [expandedMenus, setExpandedMenus] = useState({});
  const { user, logout } = useAuth();
  const { condominioSelecionado, condominios, selecionarCondominio, getCondominioNome, rotuloTipo, isAdministrador, isMorador, deveMostrarSeletor } = useCondominio();
  const location = useLocation();
  const navigate = useNavigate();
  const profileDropdownRef = useRef(null);

  // Fechar dropdowns quando clicar fora
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (profileDropdownRef.current && !profileDropdownRef.current.contains(event.target)) {
        setShowProfileDropdown(false);
      }
      if (!event.target.closest('.condominio-selector')) {
        setShowCondominioSelector(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const navigation = [
    { name: 'Dashboard', href: '/dashboard', icon: Home },
    {
      name: 'Condomínio',
      icon: Building2,
      children: [
        { name: 'Condomínios', href: '/condominios', icon: Building2, roles: ['administrador'] },
        { name: 'Síndico do Condomínio', href: '/condominios/sindico', icon: UserCog, roles: ['administrador'] },
        { name: 'Meu Condomínio', href: '/meu-condominio', icon: Users },
        { name: 'Unidades', href: '/apartamentos', icon: Building2, roles: ['administrador', 'sindico', 'subsindico', 'porteiro'] },
      ]
    },
    { 
      name: 'Portaria', 
      icon: Shield, 
      children: [
        { name: 'Encomendas', href: '/encomendas', icon: Package, roles: ['administrador', 'sindico', 'subsindico', 'porteiro', 'morador', 'usuario'] },
        { name: 'Visitantes', href: '/visitantes', icon: UserCheck, roles: ['administrador', 'sindico', 'subsindico', 'porteiro', 'morador', 'usuario'] },
      ]
    },
    { 
      name: 'Comunicação', 
      icon: MessageSquare, 
      children: [
        { name: 'Comunicados', href: '/comunicados', icon: MessageSquare, moradorReadOnly: true, roles: ['administrador', 'sindico', 'subsindico', 'porteiro', 'morador', 'usuario'] },
        { name: 'Votações', href: '/votacoes', icon: Vote, roles: ['administrador', 'sindico', 'subsindico', 'morador', 'usuario'] },
      ]
    },
    { 
      name: 'Serviços', 
      icon: Settings, 
      children: [
        { name: 'Agenda do Condomínio', href: '/agenda', icon: Calendar, roles: ['administrador', 'sindico', 'subsindico', 'porteiro', 'morador', 'usuario'] },
        { name: 'Reserva de Espaço', href: '/reservas', icon: Calendar, roles: ['administrador', 'sindico', 'subsindico', 'porteiro', 'morador', 'usuario'] },
        { name: 'Garagens', href: '/garagens', icon: Car, roles: ['administrador'] },
        { name: 'Ocorrências', href: '/ocorrencias', icon: AlertTriangle, moradorReadOnly: true, roles: ['administrador', 'sindico', 'subsindico', 'porteiro', 'morador', 'usuario'] },
      ]
    },
    { 
      name: 'Cadastros', 
      icon: Users, 
      children: [
        { name: 'Moradores', href: '/moradores', icon: Users, roles: ['administrador', 'sindico', 'subsindico', 'porteiro', 'morador', 'usuario'] },
        { name: 'Fornecedores', href: '/fornecedores', icon: Briefcase, roles: ['administrador', 'sindico', 'subsindico'] },
        { name: 'Pets', href: '/pets', icon: Heart, roles: ['administrador', 'sindico', 'subsindico', 'porteiro', 'morador', 'usuario'] },
        { name: 'Veículos', href: '/veiculos', icon: Car, roles: ['administrador', 'sindico', 'subsindico', 'porteiro', 'morador', 'usuario'] },
        { name: 'Usuários', href: '/usuarios', icon: UserPlus, roles: ['administrador', 'sindico', 'subsindico'] },
        { name: 'Funcionários', href: '/funcionarios', icon: UserCog, roles: ['administrador', 'sindico', 'subsindico'] },
        { name: 'Áreas Comuns', href: '/areas-comuns', icon: MapPin, roles: ['administrador', 'sindico', 'subsindico', 'porteiro'] },
      ]
    },
    { 
      name: 'Geral', 
      icon: FileText, 
      children: [
        { name: 'Achados e Perdidos', href: '/achados-perdidos', icon: Search, roles: ['administrador', 'sindico', 'subsindico', 'porteiro', 'morador', 'usuario'] },
        { name: 'Documentos', href: '/documentos', icon: FileText, roles: ['administrador', 'sindico', 'subsindico', 'porteiro', 'morador', 'usuario'] },
      ]
    },
    {
      name: 'Financeiro',
      icon: DollarSign,
      roles: ['administrador', 'sindico', 'subsindico', 'financeiro'],
      children: [
        { name: 'Cobranças', href: '/financeiro/cobrancas', icon: Receipt, roles: ['administrador', 'sindico', 'subsindico', 'financeiro'] },
        { name: 'Notas Fiscais', href: '/notas-fiscais', icon: FileText, roles: ['administrador', 'sindico', 'subsindico', 'financeiro'] },
        { name: 'Balancetes', href: '/balancetes', icon: BarChart3, roles: ['administrador', 'sindico', 'subsindico', 'financeiro'] },
      ]
    },
    { name: 'Meus Boletos', href: '/meus-boletos', icon: CreditCard, roles: ['morador', 'usuario', 'sindico', 'subsindico'] },
    { name: 'Relatórios', href: '/relatorios', icon: BarChart3, roles: ['administrador', 'sindico', 'subsindico'] },
    {
      name: 'Configurações',
      icon: Settings,
      roles: ['administrador', 'financeiro'],
      children: [
        { name: 'Gerais', href: '/configuracoes/gerais', icon: Settings, roles: ['administrador'] },
        { name: 'Perfil', href: '/configuracoes/perfil', icon: UserCog, roles: ['administrador'] },
        { name: 'Parâmetros', href: '/configuracoes/parametros', icon: SlidersHorizontal, roles: ['administrador', 'financeiro'] },
        { name: 'Contratos', href: '/contratos', icon: FileText, roles: ['administrador'] },
      ]
    },
  ];

  const toggleMenu = (menuName) => {
    setExpandedMenus(prev => {
      const isCurrentlyExpanded = prev[menuName];
      
      if (isCurrentlyExpanded) {
        // Se o menu está expandido, apenas fechá-lo
        return {
          ...prev,
          [menuName]: false
        };
      } else {
        // Se o menu não está expandido, fechar todos os outros e abrir este
        return {
          [menuName]: true
        };
      }
    });
  };

  const hasAccessToItem = (item) => {
    // Se tem roles definidas, verifica se o usuário tem permissão
    if (item.roles) {
      return item.roles.includes(user?.tipo);
    }
    
    // Se não tem roles definidas, permite acesso para todos
    return true;
  };

  const filteredNavigation = navigation.filter(item => {
    // Se o item está oculto, não mostra
    if (item.hidden) {
      return false;
    }

    // Se tem filhos, verifica se pelo menos um filho tem acesso
    if (item.children) {
      return item.children.some(child => hasAccessToItem(child) && !child.hidden);
    }

    return hasAccessToItem(item);
  });

  const isActive = (path) => location.pathname === path;

  return (
    <div className="min-h-screen bg-primary-50">
      {/* Mobile sidebar */}
      <div className={`fixed inset-0 z-50 lg:hidden ${sidebarOpen ? 'block' : 'hidden'}`}>
        <div
          className="fixed inset-0 bg-primary-900/80 backdrop-blur-sm"
          onClick={() => setSidebarOpen(false)}
        />
        <div className="fixed inset-y-0 left-0 flex w-64 flex-col bg-primary-900 text-primary-100 shadow-2xl min-h-0">
          <div className="relative flex h-32 items-center justify-center px-4">
            <img src={logo} alt="Logo CondoSmart" className="h-28 w-auto drop-shadow-2xl" />
            <button
              onClick={() => setSidebarOpen(false)}
              className="absolute right-4 text-primary-200 hover:text-white transition-colors"
            >
              <X className="h-6 w-6" />
            </button>
          </div>
          <nav className="sidebar-scroll flex-1 space-y-1 px-2 py-4 min-h-0">
            {filteredNavigation.map((item) => {
              const Icon = item.icon;
              
              if (item.children) {
                const isExpanded = expandedMenus[item.name];
                const hasActiveChild = item.children.some(child => isActive(child.href));
                
                return (
                  <div key={item.name}>
                    <button
                      onClick={() => toggleMenu(item.name)}
                      className={`group flex items-center justify-between w-full px-3 py-2 text-sm font-semibold rounded-lg transition-colors ${
                        hasActiveChild
                          ? 'bg-primary-700 text-white shadow-inner'
                          : 'text-primary-100 hover:bg-primary-800 hover:text-white'
                      }`}
                    >
                      <div className="flex items-center">
                        <Icon className="mr-3 h-5 w-5 text-accent-400 group-hover:text-accent-300" />
                        {item.name}
                      </div>
                      {isExpanded ? (
                        <ChevronDown className="h-4 w-4 text-accent-300" />
                      ) : (
                        <ChevronRight className="h-4 w-4 text-accent-300" />
                      )}
                    </button>
                    
                    {isExpanded && (
                      <div className="ml-4 space-y-1">
                        {item.children.filter(child => hasAccessToItem(child) && !child.hidden).map((child) => {
                          const ChildIcon = child.icon;
                          return (
                            <Link
                              key={child.name}
                              to={child.href}
                              className={`group flex items-center px-3 py-2 text-sm font-medium rounded-lg transition-colors ${
                                isActive(child.href)
                                  ? 'bg-primary-700 text-white shadow-inner'
                                  : 'text-primary-100 hover:bg-primary-800 hover:text-white'
                              }`}
                              onClick={() => setSidebarOpen(false)}
                            >
                              <ChildIcon className="mr-3 h-4 w-4 text-accent-400 group-hover:text-accent-300" />
                              {child.name}
                            </Link>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              }
              
              return (
                <Link
                  key={item.name}
                  to={item.href}
                  className={`group flex items-center px-3 py-2 text-sm font-medium rounded-lg transition-colors ${
                    isActive(item.href)
                      ? 'bg-primary-700 text-white shadow-inner'
                      : 'text-primary-100 hover:bg-primary-800 hover:text-white'
                  }`}
                  onClick={() => setSidebarOpen(false)}
                >
                  <Icon className="mr-3 h-5 w-5 text-accent-400 group-hover:text-accent-300" />
                  {item.name}
                </Link>
              );
            })}
          </nav>
        </div>
      </div>

      {/* Desktop sidebar */}
      <div className="hidden lg:fixed lg:inset-y-0 lg:flex lg:w-64 lg:flex-col min-h-0">
        <div className="flex flex-col flex-grow bg-primary-900 text-primary-100 shadow-2xl min-h-0">
          <div className="flex h-40 items-center justify-center px-6">
            <img src={logo} alt="Logo CondoSmart" className="h-32 w-auto drop-shadow-2xl" />
          </div>
          <nav className="sidebar-scroll flex-1 space-y-1 px-2 py-4 min-h-0">
            {filteredNavigation.map((item) => {
              const Icon = item.icon;
              
              if (item.children) {
                const isExpanded = expandedMenus[item.name];
                const hasActiveChild = item.children.some(child => isActive(child.href));
                
                return (
                  <div key={item.name}>
                    <button
                      onClick={() => toggleMenu(item.name)}
                      className={`group flex items-center justify-between w-full px-3 py-2 text-sm font-semibold rounded-lg transition-colors ${
                        hasActiveChild
                          ? 'bg-primary-700 text-white shadow-inner'
                          : 'text-primary-100 hover:bg-primary-800 hover:text-white'
                      }`}
                    >
                      <div className="flex items-center">
                        <Icon className="mr-3 h-5 w-5 text-accent-400 group-hover:text-accent-300" />
                        {item.name}
                      </div>
                      {isExpanded ? (
                        <ChevronDown className="h-4 w-4 text-accent-300" />
                      ) : (
                        <ChevronRight className="h-4 w-4 text-accent-300" />
                      )}
                    </button>
                    
                    {isExpanded && (
                      <div className="ml-4 space-y-1">
                        {item.children.filter(child => hasAccessToItem(child) && !child.hidden).map((child) => {
                          const ChildIcon = child.icon;
                          return (
                            <Link
                              key={child.name}
                              to={child.href}
                              className={`group flex items-center px-3 py-2 text-sm font-medium rounded-lg transition-colors ${
                                isActive(child.href)
                                  ? 'bg-primary-700 text-white shadow-inner'
                                  : 'text-primary-100 hover:bg-primary-800 hover:text-white'
                              }`}
                            >
                              <ChildIcon className="mr-3 h-4 w-4 text-accent-400 group-hover:text-accent-300" />
                              {child.name}
                            </Link>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              }
              
              return (
                <Link
                  key={item.name}
                  to={item.href}
                  className={`group flex items-center px-3 py-2 text-sm font-medium rounded-lg transition-colors ${
                    isActive(item.href)
                      ? 'bg-primary-700 text-white shadow-inner'
                      : 'text-primary-100 hover:bg-primary-800 hover:text-white'
                  }`}
                >
                  <Icon className="mr-3 h-5 w-5 text-accent-400 group-hover:text-accent-300" />
                  {item.name}
                </Link>
              );
            })}
          </nav>
        </div>
      </div>

      {/* Main content */}
      <div className="lg:pl-64">
        {/* Header */}
        <header className="bg-primary-700 text-white shadow-lg">
          <div className="flex h-16 items-center justify-between px-4 sm:px-6 lg:px-8">
            <div className="flex items-center">
              <button
                type="button"
                className="lg:hidden text-white hover:text-accent-300 transition-colors"
                onClick={() => setSidebarOpen(true)}
              >
                <Menu className="h-6 w-6" />
              </button>
              {/* Seletor de Condomínio para Administradores com múltiplos condomínios */}
              {deveMostrarSeletor() && (
                <div className="relative ml-4 lg:ml-0 condominio-selector">
                  <button
                    onClick={() => setShowCondominioSelector(!showCondominioSelector)}
                    className="flex items-center space-x-2 px-3 py-2 text-sm font-semibold text-white bg-primary-600 border border-primary-500 rounded-md hover:bg-primary-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-accent-400 focus:ring-offset-primary-700 transition-colors"
                    style={{ marginTop: '8px' }}
                  >
                    <Building2 className="h-4 w-4 text-accent-200" />
                    <span>{getCondominioNome()}</span>
                    <svg className="h-4 w-4 text-accent-200" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>
                  
                  {showCondominioSelector && (
                    <div className="absolute left-0 z-10 mt-2 w-80 origin-top-left bg-white border border-primary-200 rounded-md shadow-xl">
                      <div className="py-1">
                        {condominios.map((condominio) => (
                          <button
                            key={condominio.condominio_id}
                            onClick={() => {
                              selecionarCondominio(condominio);
                              setShowCondominioSelector(false);
                            }}
                            className={`w-full text-left px-4 py-2 text-sm hover:bg-primary-50 ${
                              String(condominioSelecionado?.condominio_id) === String(condominio.condominio_id)
                                ? 'bg-primary-100 text-primary-700'
                                : 'text-primary-700'
                            }`}
                          >
                            <div className="font-medium">{condominio.condominio_nome}</div>
                            <div className="text-xs text-gray-500">
                              {[rotuloTipo?.[condominio.tipo_acesso], [condominio.condominio_cidade, condominio.condominio_estado].filter(Boolean).join(' - ')].filter(Boolean).join(' · ')}
                            </div>
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>

            <div className="flex items-center space-x-4">
              {/* Notifications */}
              <button className="p-2 text-white/80 hover:text-accent-300 transition-colors">
                <Bell className="h-5 w-5" />
              </button>

              {/* Profile dropdown */}
              <div className="relative" ref={profileDropdownRef}>
                <button
                  onClick={() => setShowProfileDropdown(!showProfileDropdown)}
                  className="flex items-center space-x-3 hover:bg-primary-600/60 rounded-lg p-2 transition-colors"
                >
                  <div className="text-right">
                    <p className="text-sm font-semibold text-white">{user?.nome}</p>
                    <p className="text-xs text-accent-200 capitalize">{user?.tipo}</p>
                  </div>
                  <div className="h-8 w-8 rounded-full bg-accent-500 flex items-center justify-center shadow-inner shadow-primary-900/30">
                    <span className="text-sm font-semibold text-primary-900">
                      {user?.nome?.charAt(0).toUpperCase()}
                    </span>
                  </div>
                  <ChevronDown className="h-4 w-4 text-accent-200" />
                </button>

                {showProfileDropdown && (
                  <div className="absolute right-0 mt-2 w-48 bg-white rounded-md shadow-xl py-1 z-50 border border-primary-100">
                    <Link
                      to="/politica-privacidade"
                      className="block px-4 py-2 text-sm text-primary-700 hover:bg-primary-50"
                      onClick={() => setShowProfileDropdown(false)}
                    >
                      <Shield className="inline h-4 w-4 mr-2" />
                      Política de Privacidade
                    </Link>
                    <Link
                      to="/exclusao-conta"
                      className="block px-4 py-2 text-sm text-primary-700 hover:bg-primary-50"
                      onClick={() => setShowProfileDropdown(false)}
                    >
                      <UserX className="inline h-4 w-4 mr-2" />
                      Solicitar exclusão de conta
                    </Link>
                    <Link
                      to="/alterar-senha"
                      className="block px-4 py-2 text-sm text-primary-700 hover:bg-primary-50"
                      onClick={() => setShowProfileDropdown(false)}
                    >
                      <Settings className="inline h-4 w-4 mr-2" />
                      Alterar Senha
                    </Link>
                    <button
                      onClick={() => {
                        setShowProfileDropdown(false);
                        logout();
                      }}
                      className="block w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50"
                    >
                      <LogOut className="inline h-4 w-4 mr-2" />
                      Sair
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </header>

        {/* Page content */}
        <main className="p-4 sm:p-6 lg:p-8">
          {children}
        </main>
      </div>
    </div>
  );
};

export default Layout;
